import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Receipt } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatCurrency, formatDateTime } from "@/lib/format";

type InvoicePayment = components["schemas"]["InvoicePaymentResponse"];

interface InvoicePaymentsTableProps {
  organisationId?: string;
  invoiceId?: string;
}

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status.toLowerCase()) {
    case "succeeded":
      return "default";
    case "pending":
    case "processing":
      return "secondary";
    case "failed":
    case "canceled":
    case "cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

function riskVariant(level?: string | null): "default" | "secondary" | "outline" | "destructive" {
  switch (level) {
    case "normal":
      return "default";
    case "elevated":
      return "secondary";
    case "highest":
      return "destructive";
    default:
      return "outline";
  }
}

export function InvoicePaymentsTable({ organisationId, invoiceId }: InvoicePaymentsTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [payments, setPayments] = useState<InvoicePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<InvoicePayment | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPayments() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/invoice-payments", {
        params: {
          query: {
            OrganisationId: organisationId,
            InvoiceId: invoiceId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load invoice payments.");
      } else {
        setPayments(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchPayments();
    return () => {
      cancelled = true;
    };
  }, [organisationId, invoiceId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Invoice</TableHead>
              {!organisationId && <TableHead>Organisation</TableHead>}
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Succeeded</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={organisationId ? 7 : 8}
                  className="text-muted-foreground h-24 text-center"
                >
                  No payments found.
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow
                  key={payment.id}
                  className="cursor-pointer"
                  onClick={() => setSelected(payment)}
                >
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {payment.id?.slice(0, 8)}...
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {payment.invoiceId ? (
                      <Link
                        to={`/invoices/${payment.invoiceId}${
                          payment.organisationId
                            ? `?organisationId=${payment.organisationId}`
                            : ""
                        }`}
                      >
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {payment.invoiceId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {!organisationId && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {payment.organisationId ? (
                        <Link to={`/organisations/${payment.organisationId}`}>
                          <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                            {payment.organisationId.slice(0, 8)}...
                          </Badge>
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>{formatCurrency(payment.amount, payment.currency)}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(payment.status)}>{payment.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <MethodSummary payment={payment} />
                  </TableCell>
                  <TableCell>
                    {payment.risk?.level ? (
                      <Badge variant={riskVariant(payment.risk.level)} className="capitalize">
                        {payment.risk.level}
                        {payment.risk.score != null ? ` · ${payment.risk.score}` : ""}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(payment.succeededAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      <PaymentDetailsSheet
        payment={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function MethodSummary({ payment }: { payment: InvoicePayment }) {
  const pm = payment.paymentMethod;
  if (pm?.type === "card" && pm.brand && pm.last4) {
    const brand = pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1);
    return (
      <span className="text-sm">
        {brand} •••• {pm.last4}
      </span>
    );
  }
  if (pm?.type) {
    return <Badge variant="outline">{pm.type}</Badge>;
  }
  return (
    <Badge variant="outline" className="text-xs">
      {payment.provider}
    </Badge>
  );
}

function PaymentDetailsSheet({
  payment,
  onClose,
}: {
  payment: InvoicePayment | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={payment !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        {payment ? <PaymentDetailsBody payment={payment} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function PaymentDetailsBody({ payment }: { payment: InvoicePayment }) {
  const pm = payment.paymentMethod;
  const billing = payment.billing;
  const risk = payment.risk;
  const stripeBase = "https://dashboard.stripe.com/test";
  const invoiceHref = payment.invoiceId
    ? `/invoices/${payment.invoiceId}${
        payment.organisationId ? `?organisationId=${payment.organisationId}` : ""
      }`
    : null;
  const orgHref = payment.organisationId ? `/organisations/${payment.organisationId}` : null;
  const netAmount =
    typeof payment.amount === "number" && typeof payment.applicationFeeAmount === "number"
      ? payment.amount - payment.applicationFeeAmount
      : null;

  return (
    <>
      <SheetHeader>
        <SheetTitle>Payment details</SheetTitle>
        <SheetDescription>
          {formatCurrency(payment.amount, payment.currency)} · {payment.status}
        </SheetDescription>
      </SheetHeader>

      <div className="px-4 pb-6 space-y-6">
        <DetailSection title="Payment">
          <DetailRow label="Payment ID">
            <CodeValue value={payment.id ?? "—"} />
          </DetailRow>
          <DetailRow label="Invoice">
            {invoiceHref && payment.invoiceId ? (
              <Link to={invoiceHref} className="hover:underline">
                <code className="font-mono text-xs">{payment.invoiceId.slice(0, 8)}...</code>
              </Link>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </DetailRow>
          <DetailRow label="Organisation">
            {orgHref && payment.organisationId ? (
              <Link to={orgHref} className="hover:underline">
                <code className="font-mono text-xs">{payment.organisationId.slice(0, 8)}...</code>
              </Link>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </DetailRow>
          <DetailRow label="Provider">
            <Badge variant="outline" className="text-xs">{payment.provider}</Badge>
          </DetailRow>
          <DetailRow label="Status">
            <Badge variant={statusVariant(payment.status)}>{payment.status}</Badge>
          </DetailRow>
          <DetailRow label="Amount">
            {formatCurrency(payment.amount, payment.currency)}
          </DetailRow>
          {typeof payment.applicationFeeAmount === "number" && payment.applicationFeeAmount > 0 ? (
            <>
              <DetailRow label="Application fee">
                {formatCurrency(payment.applicationFeeAmount, payment.currency)}
              </DetailRow>
              {netAmount !== null ? (
                <DetailRow label="Net to SMB">
                  {formatCurrency(netAmount, payment.currency)}
                </DetailRow>
              ) : null}
            </>
          ) : null}
        </DetailSection>

        <DetailSection title="Payment method">
          {pm?.type === "card" ? (
            <>
              <DetailRow label="Type">
                <span className="capitalize">Card</span>
              </DetailRow>
              <DetailRow label="Card">
                {pm.brand} •••• {pm.last4}
              </DetailRow>
              {pm.expMonth && pm.expYear ? (
                <DetailRow label="Expires">
                  {String(pm.expMonth).padStart(2, "0")}/{String(pm.expYear).slice(-2)}
                </DetailRow>
              ) : null}
              {pm.funding ? (
                <DetailRow label="Funding">
                  <span className="capitalize">{pm.funding}</span>
                </DetailRow>
              ) : null}
              {pm.country ? <DetailRow label="Country">{pm.country}</DetailRow> : null}
              {pm.wallet ? (
                <DetailRow label="Wallet">
                  <span className="capitalize">{pm.wallet.replace("_", " ")}</span>
                </DetailRow>
              ) : null}
              {pm.id ? (
                <DetailRow label="PM ID">
                  <CodeValue value={pm.id} />
                </DetailRow>
              ) : null}
            </>
          ) : pm?.type ? (
            <DetailRow label="Type">{pm.type}</DetailRow>
          ) : (
            <DetailRow label="Provider">{payment.provider}</DetailRow>
          )}
        </DetailSection>

        {billing?.name || billing?.email ? (
          <DetailSection title="Billing">
            {billing.name ? <DetailRow label="Name">{billing.name}</DetailRow> : null}
            {billing.email ? <DetailRow label="Email">{billing.email}</DetailRow> : null}
          </DetailSection>
        ) : null}

        {risk?.level || risk?.score != null ? (
          <DetailSection title="Risk">
            {risk.level ? (
              <DetailRow label="Level">
                <Badge variant={riskVariant(risk.level)} className="capitalize">
                  {risk.level}
                </Badge>
              </DetailRow>
            ) : null}
            {risk.score != null ? <DetailRow label="Score">{risk.score}</DetailRow> : null}
          </DetailSection>
        ) : null}

        <DetailSection title="Stripe references">
          <DetailRow label="Payment intent">
            <CodeValue
              value={payment.providerReference}
              link={
                payment.provider === "Stripe"
                  ? `${stripeBase}/payments/${payment.providerReference}`
                  : undefined
              }
            />
          </DetailRow>
          {payment.chargeReference ? (
            <DetailRow label="Charge">
              <CodeValue
                value={payment.chargeReference}
                link={`${stripeBase}/payments/${payment.chargeReference}`}
              />
            </DetailRow>
          ) : null}
        </DetailSection>

        {payment.receiptUrl || payment.receiptEmail ? (
          <DetailSection title="Receipt">
            {payment.receiptEmail ? (
              <DetailRow label="Emailed to">{payment.receiptEmail}</DetailRow>
            ) : null}
            {payment.receiptUrl ? (
              <a
                href={payment.receiptUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              >
                <Receipt className="size-4" /> View receipt
              </a>
            ) : null}
          </DetailSection>
        ) : null}

        <DetailSection title="Timing">
          <DetailRow label="Succeeded">{formatDateTime(payment.succeededAt)}</DetailRow>
          <DetailRow label="Recorded">{formatDateTime(payment.createdAt)}</DetailRow>
        </DetailSection>

        {payment.notes ? (
          <DetailSection title="Notes">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{payment.notes}</p>
          </DetailSection>
        ) : null}
      </div>
    </>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {title}
      </h3>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

function CodeValue({ value, link }: { value: string; link?: string }) {
  const content = <code className="font-mono text-xs">{value}</code>;
  if (!link) return content;
  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 hover:underline"
    >
      {content}
      <ExternalLink className="size-3" />
    </a>
  );
}
